// src/store/slices/companySlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Company / branding state hydrated from the public /api/company/branding
 * endpoint (before login) and refreshed from /api/company/config on every
 * successful login (and after a Company Settings save).
 *
 * WHITE-LABEL RULE: no field ever falls back to another tenant's brand.
 * Empty string is the correct default when a value has not been configured
 * — components either render nothing OR use a neutral placeholder ("Portal",
 * "Company Logo") for identity-critical UI.
 */
export interface CompanyBranding {
  logo: string;
  loginLogo: string;
  loginBackground: string;
  favicon: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export interface CompanyState {
  // Legacy fields kept for existing consumers (Menu, Login).
  companyLogo: string;
  logowidthPrint: number;
  companyName: string;
  // Extended shape mirroring CompanyConfigService::publicBranding().
  shortName: string;
  websiteUrl: string;
  branding: CompanyBranding;
  // True once /api/company/branding OR /api/company/config has replied.
  hydrated: boolean;
}

const emptyBranding: CompanyBranding = {
  logo: '',
  loginLogo: '',
  loginBackground: '',
  favicon: '',
  primaryColor: '',
  secondaryColor: '',
  accentColor: '',
};

const initialState: CompanyState = {
  companyLogo: '',
  logowidthPrint: 15,
  companyName: '',
  shortName: '',
  websiteUrl: '',
  branding: { ...emptyBranding },
  hydrated: false,
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    /**
     * Back-compat action used by legacy Menu.tsx dispatch. Accepts the old
     * three-field shape so existing callers keep working while we migrate.
     */
    setCompanyInfo(
      state,
      action: PayloadAction<Pick<CompanyState, 'companyLogo' | 'logowidthPrint' | 'companyName'>>
    ) {
      state.companyLogo = action.payload.companyLogo;
      state.logowidthPrint = action.payload.logowidthPrint;
      state.companyName = action.payload.companyName;
      if (state.branding.logo === '' && action.payload.companyLogo) {
        state.branding.logo = action.payload.companyLogo;
      }
      state.hydrated = true;
    },
    /**
     * New action fed by /api/company/branding (public) and /api/company/config
     * (authed). Payload keys match the API response shape.
     */
    setCompanyBranding(state, action: PayloadAction<Partial<CompanyState>>) {
      const p = action.payload;
      if (p.companyName !== undefined) state.companyName = p.companyName;
      if (p.shortName !== undefined) state.shortName = p.shortName;
      if (p.websiteUrl !== undefined) state.websiteUrl = p.websiteUrl;
      if (p.branding) {
        state.branding = { ...state.branding, ...p.branding };
        if (p.branding.logo) state.companyLogo = p.branding.logo;
      }
      state.hydrated = true;
    },
    resetCompanyInfo(state) {
      // White-label: reset to EMPTY, never to another tenant's name.
      state.companyLogo = '';
      state.logowidthPrint = 15;
      state.companyName = '';
      state.shortName = '';
      state.websiteUrl = '';
      state.branding = { ...emptyBranding };
      state.hydrated = false;
    },
  },
});

export const { setCompanyInfo, setCompanyBranding, resetCompanyInfo } = companySlice.actions;
export const companyReducer = companySlice.reducer;
